import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Save, RefreshCw, Download } from "lucide-react";
import { HighlightCard as ErpHighlight } from "@/components/erp/primitives";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtINRSymbol = (n) => `₹${fmtINR(n)}`;

const inputCls =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951] bg-white";
const autoCls =
  "px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm font-mono text-right text-slate-900";

const currentPeriod = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const monthStartISO = (d = new Date()) => {
  const dt = new Date(d.getFullYear(), d.getMonth(), 1);
  return dt.toISOString().slice(0, 10);
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const rangeLabel = (fd, td) => {
  const fmt = (s) => {
    if (!s) return "";
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };
  if (fd && td) return `${fmt(fd)} → ${fmt(td)}`;
  if (fd) return `From ${fmt(fd)}`;
  if (td) return `Up to ${fmt(td)}`;
  return "";
};

const monthLabel = (p) => {
  if (!p) return "";
  const [y, m] = p.split("-");
  const dt = new Date(Number(y), Number(m) - 1, 1);
  return dt.toLocaleString("en-IN", { month: "long", year: "numeric" });
};

// ------------ Right-panel building blocks ------------
const LineRow = ({ label, value, testid, dim, negative }) => (
  <div className={`flex items-center gap-2 py-1.5 text-sm ${dim ? "text-slate-600" : "text-slate-800"}`}>
    <span className="whitespace-nowrap">{label}</span>
    <span className="flex-1 border-b border-dotted border-slate-300 mx-1"></span>
    <span
      data-testid={testid}
      className={`font-mono tabular-nums whitespace-nowrap ${negative ? "text-rose-600" : ""}`}
    >
      {fmtINRSymbol(value)}
    </span>
  </div>
);

const TotalRow = ({ label, value, testid, tint }) => (
  <div
    className={`flex items-center gap-2 py-2 px-3 -mx-3 rounded-md mt-2 border-t border-slate-200 text-sm font-semibold ${
      tint === "rose" ? "bg-rose-50/60 text-rose-900" :
      tint === "amber" ? "bg-amber-50/70 text-amber-900" :
      tint === "sky" ? "bg-sky-50/60 text-sky-900" :
      "bg-slate-50 text-slate-900"
    }`}
  >
    <span className="whitespace-nowrap">{label}</span>
    <span className="flex-1 border-b border-dotted border-slate-400 mx-1"></span>
    <span data-testid={testid} className="font-mono tabular-nums whitespace-nowrap">
      {fmtINRSymbol(value)}
    </span>
  </div>
);

const HighlightRow = ({ label, value, testid, positive, bold, subtitle }) => (
  <ErpHighlight
    title={label}
    subtitle={subtitle}
    value={fmtINRSymbol(value)}
    tone={positive ? "emerald" : "sky"}
    auto
    bold={bold}
    testid={testid}
  />
);

const SectionHeader = ({ label, tint }) => (
  <div className={`text-[11px] uppercase tracking-[0.15em] font-bold mt-5 mb-2 pb-1 border-b border-slate-200 ${
    tint === "rose" ? "text-rose-700" :
    tint === "sky" ? "text-sky-700" :
    tint === "emerald" ? "text-emerald-700" :
    "text-slate-700"
  }`}>
    {label}
  </div>
);

export default function MR() {
  const [period, setPeriod] = useState(currentPeriod());
  // Date-range scope (user-facing default = current month, first-of-month → today)
  const [fromDate, setFromDate] = useState(monthStartISO());
  const [toDate, setToDate] = useState(todayISO());
  const [companyFilter, setCompanyFilter] = useState(""); // "" = All companies
  const [companyOptions, setCompanyOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // ---- LEFT panel state (UNCHANGED from original) ----
  const [cfg, setCfg] = useState({
    opening_rm: 0,
    closing_rm: 0,
    mode: "actual",      // 'ratio' | 'actual'
    ratio_value: 0,      // percentage (0-100)
  });
  const [summary, setSummary] = useState(null);

  // ---- RIGHT panel state (Contribution & Profit Analysis) ----
  const [pnl, setPnl] = useState(null);

  const reload = async (p = period, cmp = companyFilter, fd = fromDate, td = toDate) => {
    setLoading(true);
    try {
      // Left panel still keys on `period` (single-month MR config). Right panel
      // & summary use the from/to range when both dates are provided.
      const summaryParams = { period: p };
      const pnlParams = {};
      if (fd && td) {
        summaryParams.from_date = fd;
        summaryParams.to_date = td;
        pnlParams.from_date = fd;
        pnlParams.to_date = td;
      } else {
        pnlParams.period = p;
      }
      if (cmp) {
        summaryParams.company = cmp;
        pnlParams.company = cmp;
      }
      const [{ data: c }, { data: s }, { data: pn }] = await Promise.all([
        api.get(`/mr/config/${p}`),
        api.get(`/mr/summary`, { params: summaryParams }),
        api.get(`/mr/pnl`, { params: pnlParams }),
      ]);
      setCfg({
        opening_rm: c.opening_rm || 0,
        closing_rm: c.closing_rm || 0,
        mode: c.mode || "actual",
        ratio_value: c.ratio_value || 0,
      });
      setSummary(s);
      setPnl(pn);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load MR data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(period, companyFilter, fromDate, toDate); /* eslint-disable-next-line */ }, [period, companyFilter, fromDate, toDate]);

  // Populate the company dropdown once
  useEffect(() => {
    api.get("/dashboard/companies").then((r) => setCompanyOptions(r.data || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/mr/config", {
        period,
        opening_rm: Number(cfg.opening_rm || 0),
        closing_rm: Number(cfg.closing_rm || 0),
        mode: cfg.mode,
        ratio_value: Number(cfg.ratio_value || 0),
        expenses_without_rm: 0,  // computed on the server side now
      });
      toast.success("Saved");
      reload(period);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Live preview client-side (matches backend formula)
  const previewActualRM =
    cfg.mode === "ratio"
      ? Number(summary?.total_sales || 0) * Number(cfg.ratio_value || 0) / 100
      : Number(cfg.opening_rm || 0) + Number(summary?.purchase_rm || 0) - Number(cfg.closing_rm || 0);
  const previewExpWithoutRM =
    Number(summary?.total_purchase_value || 0) - Number(summary?.purchase_rm || 0);

  const vc = pnl?.variable_cost || {};
  const fc = pnl?.fixed_cost || {};
  const mkt = pnl?.marketing_expense || {};

  const exportCsv = () => {
    if (!pnl) return;
    const rows = [
      ["Section", "Line Item", "Amount (INR)"],
      ["Sales", "Sales", pnl.sales],
      ["Variable Cost", "Raw Material", vc.raw_material || 0],
      ["Variable Cost", "In-house Cost", vc.in_house_cost || 0],
      ["Variable Cost", "Packaging Cost", vc.packaging_cost || 0],
      ["Variable Cost", "Transport Cost", vc.transport_cost || 0],
      ["Variable Cost", "Outsourcing Cost", vc.outsourcing_cost || 0],
      ["Variable Cost", "Total Variable Cost", vc.total || 0],
      ["Contribution Margin", "Contribution Margin", pnl.contribution_margin || 0],
      ["Fixed Cost", "Staff Salary", fc.staff_salary || 0],
      ["Fixed Cost", "Rent", fc.rent || 0],
      ["Fixed Cost", "Electricity", fc.electricity || 0],
      ["Fixed Cost", "EMI", fc.emi || 0],
      ["Fixed Cost", "Wages", fc.wages || 0],
      ["Fixed Cost", "Total Fixed Cost", fc.total || 0],
      ["EBITDA", "EBITDA", pnl.ebitda || 0],
      ["Marketing", "Marketing Salaries", mkt.marketing_salaries || 0],
      ["Marketing", "Travelling to Meet Customer", mkt.travelling || 0],
      ["Marketing", "Exhibition Expense", mkt.exhibition_expense || 0],
      ["Marketing", "Promotion Expense", mkt.promotion_expense || 0],
      ["Marketing", "Total Marketing Expense", mkt.total || 0],
      ["Operating Profit", "Operating Profit", pnl.operating_profit || 0],
      ["Depreciation", "Depreciation", pnl.depreciation || 0],
      ["EBIT", "EBIT", pnl.ebit || 0],
    ];
    const csv = rows.map((r) => r.map((c) => (typeof c === "string" && c.includes(",") ? `"${c}"` : c)).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const scope = (fromDate && toDate) ? `${fromDate}_to_${toDate}` : period;
    a.download = `contribution_margin_${scope}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="mr-page">
      {/* ============================ HEADER ============================ */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Module · Raw Material Analytics</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 inline-flex items-center gap-2">
            <TrendingUp size={22} /> MR
          </h1>
          <p className="text-sm text-slate-500 mt-1">Inputs on the left, Contribution & Profit Analysis on the right. All figures use Basic Cost (ex-GST).</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">Company</label>
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              data-testid="mr-company-filter"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white min-w-[160px]"
            >
              <option value="">All Companies</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              data-testid="mr-from-date"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              data-testid="mr-to-date"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" title="Used only for saving the left-panel Opening/Closing config">Config Month</label>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              data-testid="mr-period"
              className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
            />
          </div>
          <button
            onClick={() => reload(period, companyFilter, fromDate, toDate)}
            disabled={loading}
            data-testid="mr-refresh"
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={exportCsv}
            disabled={!pnl}
            data-testid="mr-export"
            className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-3 py-2 rounded-md inline-flex items-center gap-1.5"
          >
            <Download size={14} /> Export
          </button>
        </div>
      </div>

      {/* ============================ TWO-COLUMN LAYOUT ============================ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ============================ LEFT — Inputs (UNCHANGED) ============================ */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="text-sm font-semibold text-slate-900 mb-3">Raw Material — Inputs</div>
          <div className="space-y-4">
            <Row label="Opening of Raw Material" hint="Manual entry">
              <input type="number" step="any" value={cfg.opening_rm}
                onChange={(e) => setCfg({ ...cfg, opening_rm: e.target.value })}
                data-testid="mr-opening" className={inputCls} />
            </Row>

            <Row label="Purchase of Raw Material" hint="Auto · GRN inwards on cost-center = Raw Material">
              <div className={autoCls} data-testid="mr-purchase">
                ₹ {fmtINR(summary?.purchase_rm)}
              </div>
            </Row>

            <Row label="Closing of Raw Material" hint="Manual entry">
              <input type="number" step="any" value={cfg.closing_rm}
                onChange={(e) => setCfg({ ...cfg, closing_rm: e.target.value })}
                data-testid="mr-closing" className={inputCls} />
            </Row>

            <div className="pt-3 border-t border-slate-100">
              <div className="text-xs font-medium text-slate-700 mb-1.5">Actual Raw Material Used <span className="text-slate-400 font-normal">(auto-calculated)</span></div>
              <div className="flex items-center gap-4 mb-2">
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="radio" name="mr-mode" value="ratio"
                    checked={cfg.mode === "ratio"}
                    onChange={() => setCfg({ ...cfg, mode: "ratio" })}
                    data-testid="mr-mode-ratio" />
                  Ratio
                </label>
                <label className="inline-flex items-center gap-2 text-sm">
                  <input type="radio" name="mr-mode" value="actual"
                    checked={cfg.mode === "actual"}
                    onChange={() => setCfg({ ...cfg, mode: "actual" })}
                    data-testid="mr-mode-actual" />
                  Actual
                </label>
              </div>
              {cfg.mode === "ratio" ? (
                <>
                  <label className="text-[11px] text-slate-500">Ratio %</label>
                  <div className="relative">
                    <input type="number" step="any" min="0" max="100" value={cfg.ratio_value}
                      onChange={(e) => setCfg({ ...cfg, ratio_value: e.target.value })}
                      data-testid="mr-ratio-value"
                      className={`${inputCls} pr-8`}
                      placeholder="60 (means 60% of sales)" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">Actual RM Used = Total Sales × Ratio %</div>
                </>
              ) : (
                <div className="text-[10px] text-slate-500">Actual RM Used = Opening + Purchase − Closing</div>
              )}
              <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-base font-mono text-right font-bold text-amber-700"
                data-testid="mr-actual-rm-preview">
                ₹ {fmtINR(previewActualRM)}
              </div>
            </div>

            <Row label="Expenses Without Raw Material" hint="Auto · Total purchases − RM purchase">
              <div className={autoCls} data-testid="mr-exp-without">
                ₹ {fmtINR(previewExpWithoutRM)}
              </div>
            </Row>

            <button onClick={save} disabled={saving} data-testid="mr-save"
              className="w-full mt-3 bg-[#1D2951] hover:bg-[#2B3A67] disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center justify-center gap-2">
              <Save size={14} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>

        {/* ============================ RIGHT — Contribution & Profit Analysis ============================ */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold text-slate-900">Contribution &amp; Profit Analysis</div>
            <div className="text-[11px] text-slate-500 font-mono">
              {companyFilter ? <span className="text-[#1D2951] font-semibold">{companyFilter}</span> : "All Companies"} · {rangeLabel(fromDate, toDate) || monthLabel(period)}
            </div>
          </div>
          {loading && (
            <div className="text-center py-10 text-slate-400 text-sm">Loading…</div>
          )}
          {!loading && pnl && (
            <>
              {/* 1. Sales */}
              <SectionHeader label="Sales" tint="sky" />
              <TotalRow label="Sales" value={pnl.sales} testid="mr-sales" tint="sky" />

              {/* 2. Variable Cost */}
              <SectionHeader label="Less: Variable Cost" tint="rose" />
              <LineRow label="Raw Material" value={vc.raw_material} testid="mr-vc-raw" negative />
              <LineRow label="In-house Cost" value={vc.in_house_cost} testid="mr-vc-inhouse" negative />
              <LineRow label="Packaging Cost" value={vc.packaging_cost} testid="mr-vc-packaging" negative />
              <LineRow label="Transport Cost" value={vc.transport_cost} testid="mr-vc-transport" negative />
              <LineRow label="Outsourcing Cost" value={vc.outsourcing_cost} testid="mr-vc-outsourcing" negative />
              <TotalRow label="Total Variable Cost" value={vc.total} testid="mr-vc-total" tint="rose" />

              {/* 3. Contribution Margin */}
              <HighlightRow label="Contribution Margin" value={pnl.contribution_margin} testid="mr-contribution-margin" positive />

              {/* 4. Fixed Cost */}
              <SectionHeader label="Less: Fixed Cost" tint="rose" />
              <LineRow label="Staff Salary" value={fc.staff_salary} testid="mr-fc-salary" negative />
              <LineRow label="Rent" value={fc.rent} testid="mr-fc-rent" negative />
              <LineRow label="Electricity" value={fc.electricity} testid="mr-fc-electricity" negative />
              <LineRow label="EMI" value={fc.emi} testid="mr-fc-emi" negative />
              <LineRow label="Wages" value={fc.wages} testid="mr-fc-wages" negative />
              {/* Catch-all — every `is_fixed_cost=True` bill whose category doesn't
                  match one of the 5 canonical buckets still shows here so the
                  MR total tallies. Hidden when zero to keep the layout tight. */}
              {Number(fc.other_fixed_cost || 0) > 0 && (
                <LineRow label="Other Fixed Cost" value={fc.other_fixed_cost} testid="mr-fc-other" negative />
              )}
              <TotalRow label="Total Fixed Cost" value={fc.total} testid="mr-fc-total" tint="rose" />

              {/* 5. EBITDA */}
              <HighlightRow label="EBITDA" value={pnl.ebitda} testid="mr-ebitda" positive />

              {/* 6. Marketing Expense */}
              <SectionHeader label="Less: Marketing Expense" tint="rose" />
              <LineRow label="Marketing Salaries" value={mkt.marketing_salaries} testid="mr-mkt-salaries" negative />
              <LineRow label="Travelling to Meet Customers" value={mkt.travelling} testid="mr-mkt-travelling" negative />
              <LineRow label="Exhibition Expense" value={mkt.exhibition_expense} testid="mr-mkt-exhibition" negative />
              <LineRow label="Promotion Expense" value={mkt.promotion_expense} testid="mr-mkt-promotion" negative />
              <TotalRow label="Total Marketing Expense" value={mkt.total} testid="mr-mkt-total" tint="rose" />

              {/* 7. Operating Profit */}
              <HighlightRow label="Operating Profit" value={pnl.operating_profit} testid="mr-operating-profit" positive />

              {/* 8. Depreciation */}
              <SectionHeader label="Less: Depreciation" tint="rose" />
              <LineRow label="Depreciation (from Asset Register)" value={pnl.depreciation} testid="mr-depreciation" negative dim />

              {/* 9. EBIT */}
              <HighlightRow
                label="EBIT · Earnings Before Interest & Tax"
                subtitle="Final operating result — Operating Profit − Depreciation"
                value={pnl.ebit}
                testid="mr-ebit"
                positive
                bold
              />

              <div className="text-[11px] text-slate-400 mt-3 text-center border-t border-slate-100 pt-3">
                Basic cost (excludes GST). Depreciation auto-fetched from the Asset Register under Accounts.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, hint, children }) => (
  <div>
    <div className="flex justify-between items-end">
      <label className="text-xs font-medium text-slate-700">{label}</label>
      {hint && <span className="text-[10px] text-slate-400">{hint}</span>}
    </div>
    <div className="mt-1">{children}</div>
  </div>
);
